import React, { ChangeEvent, Fragment, SyntheticEvent } from "react";

import { Select, SelectOptions } from "@at/design-system-elements";
import { TypeInsignia } from "@at/design-system-foundation";

import { IconDelivery } from "@at/design-system-icons";

import { getChannelSingular } from "@at/formatter-utils";
import { dataAttrTestId } from "@at/formatter-utils/lib/data-attr-utils/data-attr-utils";
import { sortLeaseOffersByPriceAndDelivery } from "@at/leasing-utils";
import { Channel } from "@at/sauron-platform-core/lib/types";

import { LeaseContractType } from "../../../../../__generated__/graphql-types";
import {
  setLeasingModal,
  setSelectedLeaseOffer,
} from "../../../../../contexts/leasing/actions/leasing.actions";
import { useLeasingDispatchContext } from "../../../../../contexts/leasing/context/leasing.context";
import { LeasingModals } from "../../../../../contexts/leasing/types/leasing.types";

import { useChannel } from "../../../../../utils/hooks/use-channel";

import { getIsOfferInStock } from "../../../../../utils/leasing/getIsOfferInStock/get-is-offer-in-stock";
import { scrollToId } from "../../../../../utils/scrolling";
import { useTracking } from "../../../../../utils/tracking/use-tracking";
import { getLeasingConfigEntity } from "../../../../../utils/tracking-util";
import { useLeasingData } from "../../hooks/useLeasingData/use-leasing-data.hook";
import * as Styled from "../../leasing-contract-customiser.styled";
import { StockToggle } from "../stock-toggle/stock-toggle";

export const CustomiseYourVehicle = () => {
  const dispatch = useLeasingDispatchContext();
  const { state, availableLeaseOffers, colourList, trimList } =
    useLeasingData();
  const { trackEvent } = useTracking();
  const channel = useChannel();
  const { selectedLeaseOffer, leaseOffersData, leaseContractType } = state;
  const leasingEntity = getLeasingConfigEntity(state);
  const channelSingular = getChannelSingular(channel);

  const isVansChannel = channel === Channel.vans;
  const isCarsChannel = channel === Channel.cars;

  const handleSelectedColour = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedColour = event.currentTarget.value;

    const leaseOfferMatchingSelectedValues = leaseOffersData?.find(
      (offer) =>
        offer.colour === selectedColour &&
        offer.upholstery === selectedLeaseOffer?.upholstery,
    );

    const updatedLeaseOffer =
      leaseOfferMatchingSelectedValues ||
      sortLeaseOffersByPriceAndDelivery(
        availableLeaseOffers.filter((offer) => offer.colour === selectedColour),
        leaseContractType,
      )[0];

    dispatch(setSelectedLeaseOffer(updatedLeaseOffer));

    trackEvent({
      trackingLabel: `terms-color-selection`,
      replaceEntities: [
        {
          ...leasingEntity,
          colour: updatedLeaseOffer.colour ?? "",
          trim: updatedLeaseOffer.upholstery ?? "",
          monthly_payment:
            updatedLeaseOffer[
              leaseContractType === LeaseContractType.personal
                ? "monthlyPrice"
                : "monthlyPriceExVat"
            ] || 0,
        },
      ],
    });
  };

  const handleSelectedTrim = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedTrim = event.currentTarget.value;

    const leaseOfferMatchingSelectedValues = leaseOffersData?.find(
      (offer) =>
        offer.colour === selectedLeaseOffer?.colour &&
        offer.upholstery === selectedTrim,
    );

    if (leaseOfferMatchingSelectedValues) {
      dispatch(setSelectedLeaseOffer(leaseOfferMatchingSelectedValues));
      trackEvent({
        trackingLabel: `terms-trim-selection`,
        replaceEntities: [
          {
            ...leasingEntity,
            trim: selectedTrim,
            monthly_payment:
              leaseOfferMatchingSelectedValues[
                leaseContractType === LeaseContractType.personal
                  ? "monthlyPrice"
                  : "monthlyPriceExVat"
              ] || 0,
          },
        ],
      });
    }
  };

  const handleOpenFactoryOrderModal = () => {
    dispatch(
      setLeasingModal({
        show: true,
        type: LeasingModals.FACTORY_ORDER,
        title: "Factory order",
      }),
    );
  };

  const colorListOptions: SelectOptions[] = colourList.map((colour) => ({
    displayValue: colour,
    value: colour,
  }));

  const trimListOptions: SelectOptions[] = trimList.map((trim) => ({
    displayValue: trim,
    value: trim,
  }));

  const selectedOrderDelivery = selectedLeaseOffer?.deliveryEstimate;

  const carHasStockInOrder = getIsOfferInStock(selectedOrderDelivery);

  return (
    <Styled.LeasingProductBordered>
      <TypeInsignia as="h2" weight="400">
        Customise your {channelSingular}
      </TypeInsignia>
      {!isVansChannel && <StockToggle />}
      <Styled.LeasingProductCustomiseVehicleFields>
        {colorListOptions && (
          <Styled.LeasingProductFormField>
            <Select
              id="colour"
              name="colour"
              label="Colour"
              onChange={handleSelectedColour}
              options={colorListOptions}
              attributes={{
                value: selectedLeaseOffer?.colour ?? "",
                "aria-describedby": "colour",
                [dataAttrTestId]: "colour-select",
              }}
              disabled={!colourList?.length}
            />
          </Styled.LeasingProductFormField>
        )}
        {trimListOptions && (
          <Styled.LeasingProductFormField>
            <Select
              id="interior"
              name="interior"
              label="Interior"
              onChange={handleSelectedTrim}
              options={trimListOptions}
              attributes={{
                value: selectedLeaseOffer?.upholstery ?? "",
                "aria-describedby": "interior",
                [dataAttrTestId]: "interior-select",
              }}
              disabled={!trimList?.length}
            />
          </Styled.LeasingProductFormField>
        )}
      </Styled.LeasingProductCustomiseVehicleFields>
      {selectedOrderDelivery && (
        <Styled.StockBanner
          variation={carHasStockInOrder ? "success" : "info"}
          customIcon={<IconDelivery title="" />}
        >
          <Fragment>
            <Styled.StockBannerTitle>
              {carHasStockInOrder ? "In stock" : "Factory order"}
            </Styled.StockBannerTitle>
            Free delivery - Estimated in{" "}
            <Styled.Weight500>{selectedOrderDelivery}</Styled.Weight500>
            {!carHasStockInOrder && (
              <>
                {" "}
                <Styled.LinkContainer
                  href="/"
                  renderAsButton
                  onInteraction={handleOpenFactoryOrderModal}
                >
                  Learn more
                </Styled.LinkContainer>
              </>
            )}
            {!carHasStockInOrder && isCarsChannel && (
              <Styled.FindAlternativeStockContainer>
                <Styled.StockBannerTitle>Too long?</Styled.StockBannerTitle>
                We have similar cars available sooner{" "}
                <Styled.LinkContainer
                  href="/"
                  renderAsButton
                  onInteraction={(e: SyntheticEvent) => {
                    e.preventDefault();
                    scrollToId("vehicle-strip");
                  }}
                >
                  View similar cars
                </Styled.LinkContainer>
              </Styled.FindAlternativeStockContainer>
            )}
          </Fragment>
        </Styled.StockBanner>
      )}
    </Styled.LeasingProductBordered>
  );
};
